import { useState, FormEvent } from 'react';
import PageShell from '../../components/feature/PageShell';
import PageHero from '../../components/feature/PageHero';

const FORM_URL = 'https://readdy.ai/api/form/d6vg8slt4s7j0tv5ifh0';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

function ContactForm() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('mensaje') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setStatus('loading');
    try {
      const data = new FormData(form);
      const body = new URLSearchParams();
      data.forEach((val, key) => body.append(key, val.toString()));

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="contacto-mf-dental"
      className="bg-white rounded-3xl p-8 border border-gray-100"
    >
      <h3 className="text-2xl font-bold text-[#0A2540] mb-2">Envíanos un mensaje</h3>
      <p className="text-gray-500 text-sm mb-7">Responderemos en menos de 24 horas hábiles.</p>

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="block text-sm font-medium text-[#0A2540] mb-2">
            Nombre completo <span className="text-rose-400">*</span>
          </label>
          <input
            type="text"
            name="nombre"
            required
            placeholder="Ej. Juan García"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A2540] placeholder-gray-400 focus:outline-none focus:border-[#3A86FF] transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0A2540] mb-2">
            Correo electrónico <span className="text-rose-400">*</span>
          </label>
          <input
            type="email"
            name="email"
            required
            placeholder="tucorreo@ejemplo.com"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A2540] placeholder-gray-400 focus:outline-none focus:border-[#3A86FF] transition-colors"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="block text-sm font-medium text-[#0A2540] mb-2">Teléfono</label>
          <input
            type="tel"
            name="telefono"
            placeholder="55 1234 5678"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A2540] placeholder-gray-400 focus:outline-none focus:border-[#3A86FF] transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0A2540] mb-2">
            Servicio de interés
          </label>
          <select
            name="servicio"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A2540] focus:outline-none focus:border-[#3A86FF] transition-colors bg-white"
          >
            <option value="">Seleccionar servicio</option>
            <option>Rehabilitación Dental</option>
            <option>Limpieza y Prevención</option>
            <option>Estética Dental / Blanqueamiento</option>
            <option>Ortodoncia</option>
            <option>Implantes Dentales</option>
            <option>Prótesis y Coronas</option>
            <option>Endodoncia</option>
            <option>Cirugía Oral</option>
            <option>Otro</option>
          </select>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-[#0A2540] mb-2">
          Mensaje <span className="text-rose-400">*</span>
        </label>
        <textarea
          name="mensaje"
          required
          rows={4}
          maxLength={500}
          placeholder="Cuéntanos brevemente tu caso o pregunta..."
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#0A2540] placeholder-gray-400 focus:outline-none focus:border-[#3A86FF] transition-colors resize-none"
        />
        <div className={`text-right text-xs mt-1 ${charCount > 450 ? 'text-rose-400' : 'text-gray-400'}`}>
          {charCount}/500
        </div>
        {charCount > 500 && (
          <p className="text-rose-400 text-xs mt-1">El mensaje no puede superar los 500 caracteres.</p>
        )}
      </div>

      {status === 'success' && (
        <div className="mb-5 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-center gap-3">
          <div className="w-6 h-6 flex items-center justify-center text-emerald-500 flex-shrink-0">
            <i className="ri-check-double-line text-lg" />
          </div>
          <p className="text-emerald-700 text-sm font-medium">
            ¡Mensaje enviado! Te contactaremos pronto.
          </p>
        </div>
      )}

      {status === 'error' && (
        <div className="mb-5 bg-rose-50 border border-rose-200 rounded-xl px-5 py-4 flex items-center gap-3">
          <div className="w-6 h-6 flex items-center justify-center text-rose-400 flex-shrink-0">
            <i className="ri-error-warning-line text-lg" />
          </div>
          <p className="text-rose-600 text-sm">Hubo un error. Intenta de nuevo o contáctanos por WhatsApp.</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'loading' || charCount > 500}
        className="w-full bg-[#0A2540] hover:bg-[#0d3060] disabled:opacity-60 text-white py-4 rounded-xl font-semibold text-sm transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
      >
        {status === 'loading' ? (
          <>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin" />
            </div>
            Enviando...
          </>
        ) : (
          <>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-send-plane-line" />
            </div>
            Enviar Mensaje
          </>
        )}
      </button>
    </form>
  );
}

export default function ContactoPage() {
  return (
    <PageShell>
      {(onOpenModal) => (
        <>
          <PageHero
            title="Contáctanos"
            highlight=""
            subtitle="Estamos aquí para ayudarte"
            bgImage="https://readdy.ai/api/search-image?query=professional%20dental%20clinic%20reception%20area%20welcoming%20front%20desk%20elegant%20modern%20interior%20blue%20white%20clean%20medical%20office%20warm%20bright%20lighting%20staff&width=1920&height=600&seq=contacto-hero-001&orientation=landscape"
            breadcrumbs={[
              { label: 'Inicio', to: '/' },
              { label: 'Contacto' },
            ]}
          />

          <section className="py-24 bg-[#f7f9fc]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Left: Form */}
                <div className="scroll-animate">
                  <ContactForm />
                </div>

                {/* Right: Info */}
                <div className="space-y-6 scroll-animate-right">
                  {/* Appointment CTA */}
                  <div className="bg-[#0A2540] rounded-3xl p-7 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-[#3A86FF]/15 rounded-full blur-[60px]" />
                    <div className="relative z-10">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#3A86FF]/20 rounded-2xl mb-5">
                        <i className="ri-calendar-check-2-line text-[#3A86FF] text-xl" />
                      </div>
                      <h3 className="text-white font-bold text-xl mb-2">Agenda tu Cita</h3>
                      <p className="text-white/60 text-sm mb-5 leading-relaxed">
                        Usa nuestro formulario de citas para reservar directamente con un especialista. Rápido y sin esperar.
                      </p>
                      <button
                        onClick={onOpenModal}
                        className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#1a6aff] text-white px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-calendar-line" />
                        </div>
                        Agendar ahora
                      </button>
                    </div>
                  </div>

                  {/* Contact details */}
                  {[
                    {
                      icon: 'ri-phone-line',
                      color: 'text-[#3A86FF]',
                      bg: 'bg-[#3A86FF]/10',
                      title: 'Teléfono',
                      content: '55 3474 8982',
                      link: 'tel:5534748982',
                    },
                    {
                      icon: 'ri-whatsapp-line',
                      color: 'text-[#25D366]',
                      bg: 'bg-[#25D366]/10',
                      title: 'WhatsApp',
                      content: '+52 55 3474 8982',
                      link: 'https://wa.me/525534748982',
                    },
                    {
                      icon: 'ri-map-pin-2-line',
                      color: 'text-amber-500',
                      bg: 'bg-amber-500/10',
                      title: 'Dirección',
                      content: 'Av. 504 #51, San Juan de Aragón, Gustavo A. Madero, CDMX',
                      link: 'https://maps.google.com?q=Av.+504+51,+San+Juan+de+Aragón,+CDMX',
                    },
                    {
                      icon: 'ri-time-line',
                      color: 'text-emerald-500',
                      bg: 'bg-emerald-500/10',
                      title: 'Horario',
                      content: 'Lun–Sáb: 10:00 a.m. – 8:00 p.m.',
                      link: null,
                    },
                  ].map((item) => (
                    <div key={item.title} className="bg-white rounded-2xl p-5 border border-gray-100 flex items-start gap-4">
                      <div className={`w-11 h-11 flex items-center justify-center ${item.bg} rounded-xl flex-shrink-0`}>
                        <i className={`${item.icon} ${item.color} text-xl`} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">{item.title}</p>
                        {item.link ? (
                          <a
                            href={item.link}
                            target={item.link.startsWith('http') ? '_blank' : undefined}
                            rel="noopener noreferrer"
                            className="text-[#0A2540] font-medium text-sm hover:text-[#3A86FF] transition-colors cursor-pointer"
                          >
                            {item.content}
                          </a>
                        ) : (
                          <p className="text-[#0A2540] font-medium text-sm">{item.content}</p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Social */}
                  <div className="bg-white rounded-2xl p-5 border border-gray-100">
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-4">Redes Sociales</p>
                    <div className="flex gap-3">
                      <a
                        href="https://www.facebook.com/MF-Dental"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-[#f7f9fc] hover:bg-[#3A86FF]/10 border border-gray-200 hover:border-[#3A86FF] text-gray-600 hover:text-[#3A86FF] px-4 py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-facebook-fill" />
                        </div>
                        Facebook
                      </a>
                      <a
                        href="https://www.instagram.com/mfdental02"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-[#f7f9fc] hover:bg-rose-50 border border-gray-200 hover:border-rose-400 text-gray-600 hover:text-rose-500 px-4 py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-instagram-line" />
                        </div>
                        Instagram
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
